import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import {TimesheetAreaName} from "./Timesheet";
import {Identifier} from "../common/Identifier";
import {TimesheetTaskPredefinedDescription} from "./TimesheetTaskPredefinedDescription";
import {Lockable} from "../common/Lockable";
import {IdAndValue} from "../common/IdAndValue";
import {Nullable} from "../common/Nullable";

/**
 * Represents the "Timesheet Task" module within the timesheet system.
 * This module facilitates privilege checks and identification for task-related actions in timesheets.
 *
 * The purpose of this module is to ensure that task-related actions on timesheets are governed
 * by appropriate user privileges, maintaining security and organizational policy compliance.
 *
 * @property identifier
 * Represents the unique identifier for this module, used to distinguish it within the system.
 *
 * @property isPrivileged
 * A function that determines whether a given user has the required privilege to perform actions
 * related to this module.
 *
 * @param authUser - The authenticated user object requesting access. This typically contains user-specific
 *                   details required for privilege validation.
 * @param privilege - The privilege level or action being checked (e.g., 'read', 'write', 'delete').
 * @return A boolean value indicating whether the user possesses the required privilege for this module.
 *
 * @throws An error if either `authUser` or `privilege` is not provided or improperly formatted.
 */
export const TimesheetTaskModule: Module = {
    identifier: "timesheet_tasks",
    isPrivileged: ( authUser, privilege) =>  isPrivileged( authUser, TimesheetAreaName, TimesheetTaskModule.identifier, privilege )
};

/**
 * route for async select component to fetch entries
 *
 * @param phaseId the selected phase, when not set, all tasks will be fetched
 * @param stageId the selected stage, when not set, the given phase id decides what is fetched. to fetch appendices without phases this is required to be set.
 */
export function timesheetTaskSelectRoute( phaseId: number = 0, stageId: number = 0 ): string {
    if( stageId === 0 ) {
        return ( phaseId > 0 ) ? `timesheet/tasks/phases/${phaseId}/select` : "timesheet/tasks/select";
    } else {
        return `timesheet/tasks/stages/${stageId}/select`
    }

}

/**
 * Generates the endpoint for selecting tasks associated with a specific stage in a timesheet.
 *
 * This method constructs a URL path used to retrieve tasks tied to a particular stage, which can be
 * utilized when filtering or managing tasks by their stages in the timesheet system.
 *
 * @param stageId - The unique identifier representing the stage for which tasks should be selected.
 *                  Must be a valid positive integer.
 * @return A string representing the dynamically generated API endpoint for the specified stage.
 * @throws Will throw an error if the stageId is invalid or not provided (if additional validation is implemented).
 */
export function timesheetTaskByStageSelectRoute( stageId: number  ): string {
    return `timesheet/tasks/stages/${ stageId }/select`;
}

/**
 * Represents a property that associates a specific task with a timesheet entry.
 *
 * This interface enables the linking of a `TimesheetTask` object to a timesheet property,
 * which is crucial for tracking task-specific details within a timesheet system.
 *
 * This is primarily used in systems that manage time tracking, ensuring that each
 * timesheet entry has a clearly defined task associated with it.
 */
export interface TimesheetTaskProp {
    timesheetTask: TimesheetTask
}

/**
 * Represents the unique identifier for a specific task within a timesheet.
 *
 * This interface exists to provide a structured way to associate and reference
 * individual tasks within a timesheet entry. It ensures that each task can be
 * uniquely identified and managed.
 *
 * @property timesheetTaskId - A unique identifier for the task, represented
 * as an `Identifier` of type `number`. This allows developers to enforce strict
 * type checks and maintain consistency when referencing tasks.
 */
export interface TimesheetTaskId {
    timesheetTaskId: Identifier<number>
}

/**
 * Represents a task within a timesheet aimed at tracking work activities.
 *
 * This entity combines metadata about the task, its associated phase, and various flags used for filtering
 * or operational purposes. It supports integrations with external work systems and maintains lifecycle states
 * such as deletion status.
 */
export interface TimesheetTask  extends TimesheetTaskId, Lockable {
    name: string
    phaseTemplate: IdAndValue<number, string>
    taskFlags: TimesheetTaskFlagMap
    isDeleted: boolean
    externalWork?: Nullable< IdAndValue<number, string> >
    externalWorkGroup?: Nullable<IdAndValue<number, string>>
}

/**
 * Represents a property structure used to encapsulate task data for timesheet requests.
 *
 * This interface exists to standardize the way task information is passed within the system, ensuring consistency and clarity when managing timesheet-related operations.
 *
 * @property task - An object that represents the details of a task. The task is defined through the `TimesheetTaskRequest` interface, which ensures that all required task-related data is encapsulated appropriately.
 */
export interface TimesheetTaskRequestProp {
    task: TimesheetTaskRequest
}

/**
 * Represents a request for task data related to timesheets, capturing
 * essential information about a task's identity, configuration, and context.
 *
 * The `TimesheetTaskRequest` is primarily used to structure and transfer
 * detailed task-related information that integrates with timesheet workflows.
 * This includes metadata such as task identification, phase details, task
 * flags, and predefined descriptions, as well as optional associations with
 * external systems.
 */
export interface TimesheetTaskRequest {
    id: number,
    name: string,
    phaseTemplate: IdAndValue<number, string>
    taskFlags: TimesheetTaskFlagMap
    predefinedDescriptions: TimesheetTaskPredefinedDescription[]
    externalWork: Nullable< IdAndValue<number, string> >
}

/**
 * Flags are important for reporting procedures,
 * they group more than one task together into the same category
 *
 * RULES:
 * the rules describe which flag cannot or must be set with another.
 * flags that are not listed below have no requirements
 *
 * SICKNESS:
 *  set INTERNAL, ABSENCE
 *  unset SICKNESS_CHILD, HOLIDAY, MATERNITY_LEAVE, HOLIDAY_UNPAID
 *
 * SICKNESS_CHILD:
 *  set INTERNAL, ABSENCE
 *  unset MATERNITY_LEAVE, SICKNESS, HOLIDAY, HOLIDAY_UNPAID
 *
 * MATERNITY_LEAVE:
 *  set INTERNAL, ABSENCE
 *  unset SICKNESS_CHILD, SICKNESS, HOLIDAY, HOLIDAY_UNPAID
 *
 * HOLIDAY:
 *  set INTERNAL, ABSENCE
 *  unset SICKNESS_CHILD, SICKNESS, MATERNITY_LEAVE, HOLIDAY_UNPAID
 *
 * HOLIDAY_UNPAID:
 *  set INTERNAL, ABSENCE
 *  unset SICKNESS_CHILD, SICKNESS, MATERNITY_LEAVE, HOLIDAY
 */
export interface TimesheetTaskFlagMap {
    [ key: string ]: TimesheetTaskFlag
}

/**
 * Represents a set of predefined flags used to categorize or annotate tasks within a timesheet.
 *
 * These flags help in identifying the nature of a task, such as whether it is related to holidays, absences,
 * internal work, or specific leave types. They ensure standardized tagging for easier reporting and analysis.
 *
 * Developers should use these flags to enforce consistent task categorization in timesheet systems.
 *
 * Supported flags:
 * - `"DESCRIPTION_MANDATORY"`: Indicates that a description is required for the task.
 * - `"SICKNESS"`: Represents a task categorized under sickness leave.
 * - `"HOLIDAY"`: Marks a task related to paid holiday leave.
 * - `"ABSENCE"`: Denotes a task associated with a general absence.
 * - `"INTERNAL"`: Used for internal or administrative tasks.
 * - `"SICKNESS_CHILD"`: Represents sickness leave specifically related to taking care of a sick child.
 * - `"MATERNITY_LEAVE"`: Indicates that the task pertains to maternity leave.
 * - `"HOLIDAY_UNPAID"`: Marks unpaid holiday leave tasks.
 */
export type TimesheetTaskFlag = "DESCRIPTION_MANDATORY"
    | "SICKNESS"
    | "HOLIDAY"
    | "ABSENCE"
    | "INTERNAL"
    | "SICKNESS_CHILD"
    | "MATERNITY_LEAVE"
    | "HOLIDAY_UNPAID"
/**
 * TimesheetTaskFlags defines a set of standardized identifiers representing different task flags
 * used within a timesheet system. These flags help classify and manage various types of employee
 * tasks or statuses, ensuring consistency and clarity in timesheet entries.
 *
 * The flags support a variety of use cases such as tracking vacation, sick leave, or internal tasks.
 * They also include specific scenarios like unpaid holidays or maternity leave, enabling detailed
 * categorization of task types.
 *
 * Technical Details:
 * - This constant object is immutable and strongly typed with TypeScript’s `as const` assertion.
 * - The values must match predefined string literals and cannot be changed after definition.
 * - Each task flag is represented as a unique key-value mapping, where both the key and value are
 *   identical strings (e.g., `"SICKNESS": "SICKNESS"`).
 *
 * Example Use Cases:
 * - A "SICKNESS" flag may signify that a logged task is categorized as an employee’s sick leave.
 * - "DESCRIPTION_MANDATORY" indicates that a task requires an additional text description.
 *
 * Constraints:
 * - Ensure the flags are used consistently across systems and match the backend or database enumeration values.
 * - Adding `DESCRIPTION_MANDATORY` should prompt developers to enforce description validation for relevant entries.
 *
 * @property SICKNESS Represents tasks logged as sick leave.
 * @property HOLIDAY Represents tasks logged as paid vacation or holiday leave.
 * @property ABSENCE Represents unspecified absence from work.
 * @property INTERNAL Represents internal or administrative tasks.
 * @property DESCRIPTION_MANDATORY Requires a description when using this flag.
 * @property SICKNESS_CHILD Represents tasks for leave due to a child's sickness.
 * @property HOLIDAY_UNPAID Represents unpaid holiday leave.
 * @property MATERNITY_LEAVE Represents maternity leave.
 */
export const TimesheetTaskFlags: TimesheetTaskFlagMap = {
    SICKNESS: "SICKNESS",
    HOLIDAY: "HOLIDAY",
    ABSENCE: "ABSENCE",
    INTERNAL: "INTERNAL",
    DESCRIPTION_MANDATORY: "DESCRIPTION_MANDATORY",
    SICKNESS_CHILD: "SICKNESS_CHILD",
    HOLIDAY_UNPAID: "HOLIDAY_UNPAID",
    MATERNITY_LEAVE: "MATERNITY_LEAVE"
} as const;